import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BASEMAP_ADDON_GIS } from '@rsos/constants/basemapAddonNames';
import { createLayerFromUrl } from '@rsos/maps/providers/esri/layers/createLayerFromUrl';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import checkLayerVisiblity from '../utils/checkLayerVisiblity';
import { getGISLayerID } from '../utils/getGISLayerID';
import { notifyEsriMapLayerError } from '../utils/notifyEsriMapLayerError';

/**
 * An Esri map GIS basemap layer hook if the agency has GIS basemap service configured.
 * @returns Object
 */
const useGISBasemapLayer = () => {
  const hasEsriMapAccess = useSelector(state =>
    checkEsriMapAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const gisIdentities = useSelector(state => state.gisServices.gisIdentities);

  const basemapService = useSelector(state => state.gisServices.basemapService);

  const mapAddons = useSelector(state => state.sinatra.ui.mapAddons);

  const [cachedBasemapLayer, setCachedBasemapLayer] = useState(null);

  // Create GIS cached-basemap layer on load.
  useEffect(() => {
    if (hasEsriMapAccess && Object.keys(basemapService).length) {
      const layerData = {
        apiKey: basemapService?.token,
        id: getGISLayerID(basemapService),
        url: basemapService.gis_service_url,
        visible: mapAddons.includes(BASEMAP_ADDON_GIS),
      };

      createLayerFromUrl(layerData)
        .then(layer => {
          if (layer?.loaded) {
            const loadedLayer = checkLayerVisiblity(layer);

            setCachedBasemapLayer({
              layer: loadedLayer,
              service: basemapService,
            });
          }
        })
        .catch(error => {
          notifyEsriMapLayerError();

          const foundIdentity = gisIdentities.find(gisIdentity =>
            gisIdentity.gisServices.find(
              gisService => gisService.id === basemapService.id,
            ),
          );

          captureExceptionWithScope(error, {
            extra: {
              errorMessage: error?.message,
              gisIdentity: {
                arcGISToken: foundIdentity?.arcGISToken,
                gisIdentityUrl: foundIdentity?.gisIdentityUrl,
              },
              gisService: basemapService,
            },
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEsriMapAccess, basemapService, gisIdentities]);

  return cachedBasemapLayer;
};

export default useGISBasemapLayer;
